import { Fragment as _Fragment, jsx as _jsx } from "react/jsx-runtime";
// eslint-disable-next-line @typescript-eslint/naming-convention
import { useCallback, useMemo, useState } from 'react';
import { createBrowserRouter } from 'react-router-dom';
import { RootErrorBoundary, Shell } from '..';
import { NotFound, useAreModulesReady } from '../../';
import { useHoistedRoutes, useRoutes } from '../../shell';
export function useAppRouter({ rootRoutes = [], shell = Shell.Business, CusLayout = undefined, BusLayout = undefined, PreloadedComponent = undefined, } = {}) {
    const isReady = useAreModulesReady();
    if ((!CusLayout || !PreloadedComponent) && shell === Shell.Customer) {
        throw new Error('Customer layout and PreloadedComponent are required for customer shell and remotes. Please visit https://dev.azure.com/SGICanDevOps/Auto%20Fund/_wiki/wikis/Auto-Fund.wiki/3067/Using-share-customer-ui to resolve.');
    }
    if (!BusLayout && shell === Shell.Business) {
        throw new Error('Business layout is required for business shell and remotes. Please visit https://dev.azure.com/SGICanDevOps/Auto%20Fund/_wiki/wikis/Auto-Fund.wiki/3073/Using-shared-business-ui to resolve.');
    }
    // Hack to reuse the same array reference through re-renders.
    const [memoizedRootRoutes] = useState(rootRoutes);
    const routes = useRoutes();
    const wrapManagedRoutes = useCallback((managedRoutes) => {
        const preloadedComponent = shell === Shell.Customer ? PreloadedComponent : _jsx(_Fragment, { children: " Loading.." });
        return {
            path: '/',
            element: shell === Shell.Customer ? CusLayout : BusLayout,
            children: [
                {
                    // Pathless route to set an error boundary inside the layout instead of outside.
                    // It's quite useful to prevent losing the layout when an unmanaged error occurs.
                    errorElement: _jsx(RootErrorBoundary, {}),
                    children: [
                        ...managedRoutes,
                        {
                            path: '*',
                            element: !isReady ? preloadedComponent : _jsx(NotFound, {}),
                        },
                    ],
                },
            ],
        };
    }, [isReady, shell]);
    // Using the useHoistedRoutes hook allow routes hoisted by modules to be rendered at the root of the router instead of under the root layout.
    // To disallow the hoisting functionality, do not use this hook.
    const hoistedRoutes = useHoistedRoutes([...routes, ...memoizedRootRoutes], {
        wrapManagedRoutes,
        // Only the following routes can be hoisted by modules.
        allowedPaths: [],
    });
    const router = useMemo(() => {
        return createBrowserRouter([...hoistedRoutes]);
    }, [hoistedRoutes]);
    return router;
}
